import { gsap } from "../gsap.js";

export default function initNavbar() {
  const navbar = document.querySelector(".navbar");
  const navbarWrapper = document.querySelector(".navbar-wrapper");

  if (!navbar) return;

  const hasItems = navbar.querySelectorAll(".has-item");
  let currentOpenItem = null;

  // Function to show mega menu
  const showMegaMenu = (item, megaMenu, megaMenuRow) => {
    const timeline = gsap.timeline();

    // Add open class to has-item
    item.classList.add("open");

    // Add has-open class to navbar-wrapper
    if (navbarWrapper) navbarWrapper.classList.add("has-open");

    // Get the actual height needed with correct padding
    gsap.set(megaMenu, {
      height: "auto",
      paddingTop:
        "calc(var(--spacing-xl) + var(--spacing-lg) + var(--spacing-sm))",
      paddingBottom: "var(--spacing-xl)",
    });
    const targetHeight = megaMenu.offsetHeight;
    gsap.set(megaMenu, {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
    });

    // Step 1: Expand height with actual calculated height
    timeline.to(megaMenu, {
      height: targetHeight,
      paddingTop:
        "calc(var(--spacing-xl) + var(--spacing-lg) + var(--spacing-sm))",
      paddingBottom: "var(--spacing-xl)",
      duration: 0.3,
      ease: "power2.out",
    });

    // Step 2: Fade in content (overlap)
    timeline.to(
      megaMenuRow,
      {
        opacity: 1,
        duration: 0.2,
        ease: "power1.out",
      },
      "-=0.15"
    );

    return timeline;
  };

  // Function to hide mega menu
  const hideMegaMenu = (item, megaMenu, megaMenuRow) => {
    const timeline = gsap.timeline();

    // Remove open class from has-item
    item.classList.remove("open");

    // Remove has-open class from navbar-wrapper
    if (navbarWrapper) navbarWrapper.classList.remove("has-open");

    // Step 1: Fade out content
    timeline.to(megaMenuRow, {
      opacity: 0,
      duration: 0.15,
      ease: "power1.in",
    });

    // Step 2: Collapse height
    timeline.to(megaMenu, {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      duration: 0.25,
      ease: "power2.in",
    });

    return timeline;
  };

  hasItems.forEach((item) => {
    const megaMenu = item.querySelector(".mega-menu");
    const megaMenuRow = megaMenu
      ? megaMenu.querySelector(".mega-menu-row")
      : null;
    const navBtn = item.querySelector(".nav-btn");

    if (!megaMenu || !megaMenuRow || !navBtn) return;

    // Set initial state
    gsap.set(megaMenu, {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
    });

    gsap.set(megaMenuRow, {
      opacity: 0,
    });

    // Toggle mega menu on click
    navBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      const isCurrentlyOpen = currentOpenItem === item;

      // Close all open items first
      if (currentOpenItem) {
        const currentMegaMenu = currentOpenItem.querySelector(".mega-menu");
        const currentMegaMenuRow =
          currentOpenItem.querySelector(".mega-menu-row");
        hideMegaMenu(currentOpenItem, currentMegaMenu, currentMegaMenuRow);
        currentOpenItem = null;
      }

      // If clicking different item, open it
      if (!isCurrentlyOpen) {
        showMegaMenu(item, megaMenu, megaMenuRow);
        currentOpenItem = item;
      }
    });
  });

  // Close mega menu when clicking outside
  document.addEventListener("click", (e) => {
    if (currentOpenItem && !currentOpenItem.contains(e.target)) {
      const megaMenu = currentOpenItem.querySelector(".mega-menu");
      const megaMenuRow = currentOpenItem.querySelector(".mega-menu-row");
      hideMegaMenu(currentOpenItem, megaMenu, megaMenuRow);
      currentOpenItem = null;
    }
  });

  // Scroll handling for navbar
  if (navbarWrapper) {
    let lastScrollY = window.scrollY;
    let ticking = false;

    function handleScroll() {
      const currentScrollY = window.scrollY;

      // Toggle .scrolled class when scroll > 50px
      if (currentScrollY > 50) {
        navbarWrapper.classList.add("scrolled");
      } else {
        navbarWrapper.classList.remove("scrolled");
      }

      // Toggle scroll direction classes
      if (currentScrollY > lastScrollY) {
        // Scrolling down
        navbarWrapper.classList.remove("scroll-up");
        navbarWrapper.classList.add("scroll-down");
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        navbarWrapper.classList.remove("scroll-down");
        navbarWrapper.classList.add("scroll-up");
      }

      lastScrollY = currentScrollY;
      ticking = false;
    }

    window.addEventListener("scroll", () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
        });
        ticking = true;
      }
    });

    // Initial call
    handleScroll();

    // ---------------------------------------------------------------
    // Sticky-navbar offset for anchor links.
    // Publish the navbar height as --navbar-height on <html>. The SCSS uses it
    // for sticky offsets (.inpage-nav, filters, etc.) and, in _navbar.scss, for
    // html { scroll-padding-top } so a #hash link (e.g. #subscribe-newsletter)
    // lands with its heading below the navbar instead of underneath it.
    // Measures .navbar-container, the same as inpageNav.js, so both agree.
    // ---------------------------------------------------------------
    const navbarContainer = navbarWrapper.querySelector(".navbar-container");

    function updateNavbarHeightVar() {
      const height = navbarContainer
        ? navbarContainer.offsetHeight
        : navbarWrapper.offsetHeight;
      document.documentElement.style.setProperty("--navbar-height", `${height}px`);
    }

    // The browser jumps to a URL #hash before this script runs, using the CSS
    // fallback height. Once the real height is known, repeat the jump so the
    // target sits exactly below the navbar. Skipped on reload / back-forward,
    // where the browser restores the previous scroll position instead.
    function realignHashTarget() {
      if (!window.location.hash) return;
      const navEntry = performance.getEntriesByType("navigation")[0];
      if (navEntry && navEntry.type !== "navigate") return;
      const id = decodeURIComponent(window.location.hash.slice(1));
      const target = id ? document.getElementById(id) : null;
      if (target) target.scrollIntoView();
    }

    updateNavbarHeightVar();
    realignHashTarget();

    // The logo image may finish loading after DOMContentLoaded and change the
    // navbar height, so measure again once everything has loaded.
    window.addEventListener("load", () => {
      updateNavbarHeightVar();
      realignHashTarget();
    });

    let resizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateNavbarHeightVar, 150);
    });
  }

  // Mobile navigation handling
  const navigation = document.querySelector(".navigation");
  const hamburgerButton = document.querySelector(".icon-hamburger");

  if (navigation && hamburgerButton) {
    const navigationItems = navigation.querySelectorAll(".has-item");
    const navigationMain = navigation.querySelector(".navigation-main");

    // Set initial state for navigation
    gsap.set(navigation, {
      y: "-100%",
    });

    gsap.set(navigationMain, {
      opacity: 0,
    });

    // Function to show navigation
    const showNavigation = () => {
      const timeline = gsap.timeline();

      navigation.classList.add("open");
      hamburgerButton.classList.add("active");
      document.body.classList.add("nav-open");

      // Step 1: Slide down navigation
      timeline.to(navigation, {
        y: "0%",
        duration: 0.4,
        ease: "power2.out",
      });

      // Step 2: Fade in navigation-main
      timeline.to(
        navigationMain,
        {
          opacity: 1,
          duration: 0.2,
          ease: "power1.out",
        },
        "-=0.15"
      );

      return timeline;
    };

    // Function to hide navigation
    const hideNavigation = () => {
      const timeline = gsap.timeline();

      // Step 1: Fade out navigation-main
      timeline.to(navigationMain, {
        opacity: 0,
        duration: 0.15,
        ease: "power1.in",
      });

      // Step 2: Slide up navigation
      timeline.to(navigation, {
        y: "-100%",
        duration: 0.4,
        ease: "power2.in",
        onComplete: () => {
          navigation.classList.remove("open");
          hamburgerButton.classList.remove("active");
          document.body.classList.remove("nav-open");

          // Reset all navigation-child to first level
          navigationItems.forEach((item) => {
            const navigationChild = item.querySelector(".navigation-child");
            if (navigationChild) {
              navigationChild.classList.remove("show");
            }
          });
        },
      });

      return timeline;
    };

    // Toggle navigation menu
    hamburgerButton.addEventListener("click", () => {
      const isOpen = navigation.classList.contains("open");

      if (isOpen) {
        hideNavigation();
      } else {
        showNavigation();
      }
    });

    navigationItems.forEach((item) => {
      const navigationChild = item.querySelector(".navigation-child");
      const backButton = navigationChild ? navigationChild.querySelector(".navigation-child-back") : null;

      if (!navigationChild) return;

      // Click on has-item to show child
      item.addEventListener("click", (e) => {
        // Only trigger if clicking on the item itself, not on child elements
        if (e.target === item || (item.contains(e.target) && !navigationChild.contains(e.target))) {
          navigationChild.classList.add("show");
        }
      });

      // Click on back button to hide child
      if (backButton) {
        backButton.addEventListener("click", (e) => {
          e.stopPropagation();
          navigationChild.classList.remove("show");
        });
      }
    });
  }
}
